import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import {
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  timeout
} from 'rxjs';
import { ConfigurationService } from '@sondermind/configuration';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { camelCaseTransformer } from '@sondermind/utilities/tools';
import { CMS_PROVIDER, CmsProvider } from './constants/cms-provider';
import { CmsContentTypes } from './constants/cms-content-types';
import {
  CmsAttributes,
  CmsRes,
  CmsResponse,
  ICmsFooterNavigation,
  ICmsLegalNavigation,
  ICmsSocialNavigation,
  ICmsGlobalText,
  IProviderMenu,
  IPublicFooterNavigationBundle,
  ICmsSitemapCity,
} from './constants/cms-dto';
import {
  FALLBACK_FOOTER_NAVIGATION,
  FALLBACK_LEGAL_NAVIGATION,
  FALLBACK_SOCIAL_NAVIGATION,
  FALLBACK_GLOBAL_TEXT,
  PUBLIC_FOOTER_NAVIGATION_DEFAULT_BUNDLE
} from './constants/cms-fallback-data';

const CMS_HTTP_TIMEOUT_MS = 5000;
const SHARE_REPLAY = { bufferSize: 1, refCount: true } as const;

@Injectable({
  providedIn: 'root'
})
export class CmsService {
  // All reads route to the provider chosen by the `cms-sanity-enabled` flag: on -> /core/sanity,
  // off (default, and on evaluation failure/SSR/CI, or when no flag service is provided) -> /core/strapi.
  private readonly apiUrl$ = (
    this.featureFlagService?.isEnabled$(FeatureFlagKey.CMS_SANITY_ENABLED) ?? of(false)
  ).pipe(
    map((useSanity) => this.cmsProxyUrl(useSanity ? CMS_PROVIDER.sanity : CMS_PROVIDER.strapi)),
    catchError(() => of(this.cmsProxyUrl(CMS_PROVIDER.strapi))),
    shareReplay(SHARE_REPLAY)
  );

  private cmsProxyUrl(provider: CmsProvider): string {
    return `${this.configService.env.svcBase}/core/${provider}`;
  }

  private cmsGet$<TRes>(params: HttpParams): Observable<TRes> {
    return this.apiUrl$.pipe(
      switchMap((apiUrl) => this.httpClient.get<TRes>(apiUrl, { params }).pipe(timeout(CMS_HTTP_TIMEOUT_MS)))
    );
  }

  // Cache for navigation content
  private footerNavigation$: Observable<ICmsFooterNavigation> | null = null;
  private legalNavigation$: Observable<ICmsLegalNavigation> | null = null;
  private socialNavigation$: Observable<ICmsSocialNavigation> | null = null;
  private globalText$: Observable<ICmsGlobalText> | null = null;
  private providers$: Observable<IProviderMenu[]> | null = null;
  private footerNavigationBundle$: Observable<IPublicFooterNavigationBundle> | null = null;
  private sitemapCities$: Map<string, Observable<ICmsSitemapCity[]>> = new Map();

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigurationService,
    @Optional() @Inject(FEATURE_FLAG_SERVICE) private featureFlagService: IFeatureFlagService | null,
    @Inject(PLATFORM_ID) private platformId: object
  ) { }

  getContent(
    contentType: CmsContentTypes,
    filterKey: string,
    filterValues: string[],
    populate?: string
  ): Observable<CmsAttributes[]> {
    let params = new HttpParams()
      .set('content_type', contentType)
      .set('filter_key', filterKey)
      .set('page_size', '50');

    if (populate) {
      params = params.set('populate', populate);
    }

    filterValues.forEach((value) => {
      params = params.append('filter_values[]', value);
    });

    return this.cmsGet$<CmsRes>(params).pipe(
      map((res: CmsRes) => {
        if (res?.data?.length) {
          return res.data.map((val) => camelCaseTransformer<CmsAttributes>(val));
        }

        return [];
      }),
      catchError(() => of([])),
      shareReplay(SHARE_REPLAY)
    );
  }

  /**
   * Fetches content from the CMS.
   * Content like footer, legal, and social navigations.
   */
  getNavigationContent<T>(
    contentType: CmsContentTypes,
    populate?: string,
    filters?: string
  ): Observable<T | null> {
    let params = new HttpParams()
      .set('content_type', contentType)
      .set('page_size', '100');

    if (populate) {
      params = params.set('populate', populate);
    }

    if (filters) {
      params = params.set('filters', filters);
    }

    return this.cmsGet$<CmsResponse<T>>(params).pipe(
      map((res: CmsResponse<T>) => {
        if (res?.data) {
          return camelCaseTransformer<T>(res.data);
        }
        return null;
      }),
      shareReplay(SHARE_REPLAY)
    );
  }

  /**
   * Public-footer CMS bundle. On SSR: synchronous default (no HTTP). On browser: live CMS.
   */
  getPublicFooterNavigationBundle$(): Observable<IPublicFooterNavigationBundle> {
    if (!this.footerNavigationBundle$) {
      if (isPlatformServer(this.platformId)) {
        this.footerNavigationBundle$ = of(PUBLIC_FOOTER_NAVIGATION_DEFAULT_BUNDLE).pipe(
          shareReplay(SHARE_REPLAY)
        );
      } else {
        this.footerNavigationBundle$ = forkJoin({
          footer: this.getFooterNavigation$(),
          legal: this.getLegalNavigation$(),
          social: this.getSocialNavigation$(),
          globalText: this.getGlobalText$(),
          providers: this.getProviders$()
        }).pipe(shareReplay(SHARE_REPLAY));
      }
    }
    return this.footerNavigationBundle$;
  }

  /**
   * Gets footer navigation menus from the CMS.
   * Returns fallback data on error or empty response.
   */
  getFooterNavigation$(): Observable<ICmsFooterNavigation> {
    if (!this.footerNavigation$) {
      this.footerNavigation$ = this.getNavigationContent<ICmsFooterNavigation>(
        CmsContentTypes.FOOTER_NAVIGATION,
        'menus'
      ).pipe(
        map((data) => data || FALLBACK_FOOTER_NAVIGATION),
        catchError(() => of(FALLBACK_FOOTER_NAVIGATION))
      );
    }
    return this.footerNavigation$;
  }

  /**
   * Gets legal navigation links from the CMS.
   * Returns fallback data on error or empty response.
   */
  getLegalNavigation$(): Observable<ICmsLegalNavigation> {
    if (!this.legalNavigation$) {
      this.legalNavigation$ = this.getNavigationContent<ICmsLegalNavigation>(
        CmsContentTypes.LEGAL_NAVIGATION,
        '*'
      ).pipe(
        map((data) => data || FALLBACK_LEGAL_NAVIGATION),
        catchError(() => of(FALLBACK_LEGAL_NAVIGATION))
      );
    }
    return this.legalNavigation$;
  }

  /**
   * Gets social navigation links from the CMS.
   * Returns fallback data on error or empty response.
   */
  getSocialNavigation$(): Observable<ICmsSocialNavigation> {
    if (!this.socialNavigation$) {
      this.socialNavigation$ = this.getNavigationContent<ICmsSocialNavigation>(
        CmsContentTypes.SOCIAL_NAVIGATION,
        '*'
      ).pipe(
        map((data) => data || FALLBACK_SOCIAL_NAVIGATION),
        catchError(() => of(FALLBACK_SOCIAL_NAVIGATION))
      );
    }
    return this.socialNavigation$;
  }

  /**
   * Gets global text from the CMS.
   * Returns fallback data on error or empty response.
   */
  getGlobalText$(): Observable<ICmsGlobalText> {
    if (!this.globalText$) {
      this.globalText$ = this.getNavigationContent<ICmsGlobalText>(
        CmsContentTypes.GLOBAL_TEXT,
        '*'
      ).pipe(
        map((data) => data || FALLBACK_GLOBAL_TEXT),
        catchError(() => of(FALLBACK_GLOBAL_TEXT))
      );
    }
    return this.globalText$;
  }

  // Fetch providers from the CMS
  getProviders$(): Observable<IProviderMenu[]> {
    if (!this.providers$) {
      this.providers$ = this.getNavigationContent(
        CmsContentTypes.PROVIDERS,
        '*'
      ).pipe(
        map((data) => (Array.isArray(data) ? data : []) as IProviderMenu[]),
        catchError(() => of([]))
      );
    }

    return this.providers$;
  }

  // Gets sitemap cities from the CMS
  getSitemapCities$(state: string): Observable<ICmsSitemapCity[]> {
    if (!this.sitemapCities$.has(state)) {
      const sitemapCities = this.getNavigationContent<ICmsSitemapCity[]>(
        CmsContentTypes.SITEMAP_CITIES,
        'state',
        `filters[state][abbreviation][$eqi]=${state}&filters[enabled][$eq]=true`
      ).pipe(
        map((data) => (data ?? [])),
        catchError(() => of([])),
        shareReplay(SHARE_REPLAY)
      );
      this.sitemapCities$.set(state, sitemapCities);
    }
    return this.sitemapCities$.get(state) ?? of([]);
  }
}
